import React, { FC, useMemo, useState } from 'react';
import { Collapse } from '@mui/material';
import fp from 'lodash/fp';

import {
    useMediaSwitch,
    Stack,
    Typography,
    useMobile,
    ArrowDown24,
    ArrowUp24,
    IconButton,
    useDesktop,
    File24,
} from 'Modules/ui3';

import { ContainerBox } from '../ContainerBox';
import { Loader } from '../ui/loaders';
import { DocumentsCommonProps, FileProps, FilesContainerProps } from './types';


const File: FC<FileProps> = ({ title, href }) => {
    const isMobile = useMobile();
    const fileType = href ? 'a' : 'span';

    return (
        <Stack direction='row' spacing={3} alignItems='center'>
            <File24
                sx={({ spacing }) => ({
                    alignSelf: 'flex-start',
                    color: href ? 'general.primary50' : 'grayscale.shade90',
                    height: spacing(6),
                    width: spacing(6),
                })}
            />
            <Typography<typeof fileType>
                variant={isMobile ? 'regularCaption1' : 'regularBody2'}
                color='grayscale.shade95'
                component={fileType}
                sx={fileType === 'a' ? {
                    '&:hover': {
                        color: 'general.primary50',
                    },
                    '&:visited': {
                        color: 'grayscale.shade95',
                        '&:hover': {
                            color: 'general.primary50',
                        },
                    },
                } : {}}
                {...href ? { target: '_blank', href } : {}}
            >
                {title}
            </Typography>
        </Stack>
    );
};

const FilesContainer: FC<FilesContainerProps> = ({
    files,
    title,
}) => {
    const isDesktop = useDesktop();
    const isMobile = useMobile();
    const filesColumns = useMemo(
        () => isDesktop ? fp.chunk(Math.ceil(files.length / 2), files) : [files],
        [files, isDesktop],
    );
    return (
        <Stack spacing={isDesktop ? 6 : 4}>
            {title && (
                <Typography
                    variant={isMobile ? 'mediumCaption1' : 'mediumBody1'}
                    color='grayscale.shade95'
                >
                    {title}
                </Typography>
            )}
            <Stack direction='row' spacing={4}>
                {filesColumns.map((col, idx) => (
                    <Stack
                        key={`col_${idx}`}
                        flex={1}
                        spacing={isMobile ? 2 : 4}
                    >
                        {col.map(file => (
                            <File
                                key={file.id}
                                href={file.file?.url}
                                title={file.header}
                            />
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};

export const DocumentsCommon: FC<DocumentsCommonProps> = ({
    documents = [],
    showLoader,
    title,
    titleButtons,
    defaultOpen = false,
    ...props
}) => {
    const isMobile = useMobile();
    const isDesktop = useDesktop();
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const handleAccordionClick = () => {
        if (!isOpen)
            setIsOpen(true);
    };

    const handleToggleButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsOpen(prev => !prev);
    };

    return (
        <ContainerBox {...props} onClick={handleAccordionClick} sx={{ cursor: isOpen ? 'default' : 'pointer' }}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={useMediaSwitch(6, 4, 2)}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                >
                    <Typography
                        fontSize={useMediaSwitch('31px', '22px', '16px')}
                        fontWeight='500'
                        lineHeight={useMediaSwitch(1.3, 1.25, 1.5)}
                        color='grayscale.shade95'
                        align={isDesktop ? 'center' : 'left'}
                    >
                        {title}
                    </Typography>
                    {(!isMobile || isOpen) && titleButtons}
                </Stack>
                <IconButton
                    variant='stroke'
                    size={useMediaSwitch('extraLarge', 'large', 'small')}
                    isRound
                    onClick={handleToggleButtonClick}
                    onMouseUp={e => e.currentTarget.blur()}
                    disableRipple
                    sx={{
                        alignSelf: 'flex-start',
                    }}
                >
                    {isOpen ? <ArrowUp24 /> : <ArrowDown24 />}
                </IconButton>
            </Stack>
            <Collapse in={isOpen}>
                <Stack pt={useMediaSwitch(8, 6, 5)} spacing={useMediaSwitch(8, 6, 5)}>
                    {showLoader ? <Loader />
                        : documents.map((group, idx) =>
                            group.files.length > 0 && (
                                <FilesContainer
                                    key={idx}
                                    files={group.files}
                                    title={group.subtitle}
                                />
                            ))
                    }

                </Stack>
            </Collapse>
        </ContainerBox>
    );
};
